import React, { useState, useEffect } from "react";
import DashboardCardItems from "./DashboardCardItems";
import OfflineEdgeDevices from "./OfflineEdgeDevices";
import SearchBar from "./SearchBar";

import moment from "moment";
import { useTheme, Tooltip, ToggleButton, ToggleButtonGroup } from "@mui/material";
import ListIcon from "@mui/icons-material/List";
import DashboardIcon from "@mui/icons-material/Dashboard";
import { Box } from "@mui/system";

const useStyles = (theme) => ({
    card: {
        borderRadius: 0,
    },
    deviceId: {
        fontSize: ".9rem",
        fontWeight: "700",
        marginBottom: ".5rem",
        textDecoration: "none",
        color: "#1976d2",
        "&:visited": {
            color: "#1976d2",
        },
    },
    dashboardCardsTitle: {
        marginTop: "2rem",
        fontWeight: "700",
        fontSize: "1.4rem",
        marginBottom: ".5rem",
        textAlign: "center",
    },
    leftToggleButton: {
        border: "2px solid #0083CA !important",
        borderRight: "2px solid #0083CA !important",
        color: "#0083CA",
        background: "#fff",
    },
    rightToggleButton: {
        border: "2px solid #0083CA !important",
        borderLeft: "2px solid #0083CA !important",
        color: "#0083CA",
        background: "#fff",
    },
    searchContainer: {
        display: "flex",
        justifyContent: "space-evenly",
        [theme.breakpoints.down("lg")]: {
            flexDirection: "column",
            alignItems: "center",
        },
    },
    layoutButtonGroup: {
        display: "flex",
        width: "100%",
        justifyContent: "flex-end",
        paddingBottom: ".5rem",
    },
    cardView: {
        "&:hover": {
            cursor: "pointer",
            color: "#1976d2",
        },
    },
    listView: {
        paddingRight: ".5rem",
        "&:hover": {
            cursor: "pointer",
            color: "#1976d2",
        },
    },
});

export default function DashboardCards(props) {
    let {
        lineType,
        edgeMessages,
        filteredMessages,
        polarisMessages,
        value,
        loading,
        offlineDevices,
        deviceIds,
        onTagsChange,
        handleLineType,
        searchGroup,
        fetchOfflineDevices,
        updateOfflineDevices,
    } = props;

    const theme = useTheme();
    const classes = useStyles(theme);
    const [rowSize] = useState(4);
    const [messages, setMessages] = useState(null);
    const [layout, setLayout] = useState("list");
    const [prevValue, setPrevValue] = useState(0);

    const messageTypes = ["edgeMessages", "rtuMessages", "polarisMessages"];
    const fields = ["compressed_count", "delta", "VBatt"];
    const typeIds = ["edge_device_id", "rtu_id", "edge_device_id"];
    const unitTypes = ["Point Count", "Delta (minutes)", "Volts"];
    const typeTitles = ["Edge Devices", "RTU Devices", "Polaris Devices"];
    const deviceTypes = ["v3", "rtu", "polaris"];
    const title = typeTitles[value];
    const searchParams = {
        0: ["edgeDeviceId", "companyName", "lineName"],
        1: ["rtuId", "companyName", "lineName"],
        2: ["polarisId", "companyName", "lineName"],
    };

    useEffect(() => {
        let messageType = messageTypes[value];
        let selectedMessages = props[messageType];

        if (prevValue !== value) {
            setPrevValue(value);
        }

        if (filteredMessages.length) {
            setMessages(filteredMessages);
        } else if (selectedMessages?.length) {
            setMessages(selectedMessages);
        }
    }, [edgeMessages, filteredMessages, polarisMessages, value, props, messageTypes, prevValue]);

    useEffect(() => {
        if (prevValue !== value) {
            fetchOfflineDevices();
        }
    }, [prevValue, value, fetchOfflineDevices]);

    const getChartOverides = () => {
        let titleText = unitTypes[value];

        return {
            title: {
                text: titleText,
                textStyle: {
                    fontSize: 12,
                },
            },
        };
    };

    const findTimeDifference = (endDttm) => {
        const startTime = moment(new Date(endDttm));
        const endTime = moment(new Date());
        const duration = moment.duration(endTime.diff(startTime));
        const minutes = duration.asMinutes();

        return minutes;
    };

    return (
        <>
            {(deviceTypes[value] === "v3" || deviceTypes[value] === "polaris") && (
                <OfflineEdgeDevices
                    deviceType={deviceTypes[value]}
                    loading={loading}
                    offlineDevices={offlineDevices}
                    updateOfflineDevices={updateOfflineDevices}
                />
            )}
            <Box component="div" sx={classes.searchContainer}>
                {searchParams[value].map((params, idx) => {
                    return (
                        <SearchBar
                            key={idx}
                            onTagsChange={onTagsChange}
                            deviceIds={deviceIds}
                            group={params}
                            searchGroup={searchGroup}
                        />
                    );
                })}
            </Box>

            {!loading && (
                <Box component="div" sx={classes.dashboardCardsTitle}>
                    {title}
                </Box>
            )}

            {!loading && (
                <div>
                    {layout === "card" && (
                        <div>
                            <ToggleButtonGroup
                                value={lineType}
                                exclusive
                                onChange={handleLineType}
                                style={{ height: "25px", marginTop: "1rem" }}
                            >
                                <ToggleButton
                                    sx={lineType === "log" ? classes.leftToggleButton : ""}
                                    style={{
                                        border: "2px solid rgba(0, 0, 0, 0.12)",
                                        borderRadius: "25px 0px 0px 25px",
                                    }}
                                    value="value"
                                >
                                    <div>Linear</div>
                                </ToggleButton>
                                <ToggleButton
                                    sx={lineType === "value" ? classes.rightToggleButton : ""}
                                    style={{
                                        border: "2px solid rgba(0, 0, 0, 0.12)",
                                        borderRadius: "0px 25px 25px 0px",
                                    }}
                                    value="log"
                                >
                                    <div>Log</div>
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </div>
                    )}
                    <Box component="div" sx={classes.layoutButtonGroup}>
                        <Box component="div" sx={classes.listView}>
                            <Tooltip title="List View">
                                <ListIcon onClick={() => setLayout("list")} />
                            </Tooltip>
                        </Box>
                        <Box component="div" sx={classes.cardView}>
                            <Tooltip title="Card View">
                                <DashboardIcon onClick={() => setLayout("card")} />
                            </Tooltip>
                        </Box>
                    </Box>
                </div>
            )}
            {!loading && messages && (
                <DashboardCardItems
                    messages={messages}
                    typeIds={typeIds}
                    value={value}
                    rowSize={rowSize}
                    findTimeDifference={findTimeDifference}
                    getChartOverides={getChartOverides}
                    lineType={lineType}
                    fields={fields}
                    layout={layout}
                />
            )}
        </>
    );
}
